import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Banknote, Footprints, Globe, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const thisMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const StaffEarnings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/staff-earnings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month },
      });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || t('staffEarnings.loadFailed', 'Could not load staff earnings'));
    } finally {
      setLoading(false);
    }
  }, [token, month, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="staff-earnings-page">
      <div className="mx-auto max-w-5xl px-4 pt-6">
        <button
          onClick={() => navigate('/provider-dashboard?tab=revenue')}
          data-testid="staff-earnings-back-btn"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('walkIn.backToRevenue', 'Back to revenue')}
        </button>

        <div className="flex flex-wrap items-end justify-between gap-3 rounded-2xl bg-white p-5 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('staffEarnings.title', 'Staff earnings')}</h1>
            <p className="text-sm text-slate-500">
              {t('staffEarnings.subtitle', 'What each team member brought in this month, online and walk-in.')}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold text-slate-500">{t('staffEarnings.month', 'Month')}</label>
            <input
              type="month"
              className="numeric-ltr rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={month}
              data-testid="staff-earnings-month"
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
        </div>

        {loading && (
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> {t('staffEarnings.loading', 'Loading earnings…')}
          </div>
        )}
        {error && <p className="mt-6 rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="staff-earnings-error">{error}</p>}

        {data && !loading && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: Globe, label: t('staffEarnings.onlineNet', 'Online (after commission)'), value: `${data.totals.online_net} ${t('common.currency', 'KWD')}`, testid: 'staff-earnings-online-net' },
                { icon: Banknote, label: t('staffEarnings.commission', 'Commission paid'), value: `${data.totals.commission} ${t('common.currency', 'KWD')}`, testid: 'staff-earnings-commission' },
                { icon: Footprints, label: t('staffEarnings.walkIns', 'Walk-ins'), value: `${data.totals.walk_in_earnings} ${t('common.currency', 'KWD')}`, testid: 'staff-earnings-walkin' },
                { icon: Banknote, label: t('staffEarnings.totalKept', 'Total you keep'), value: `${data.totals.total_net} ${t('common.currency', 'KWD')}`, testid: 'staff-earnings-total' },
              ].map((card) => (
                <div key={card.label} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={card.testid}>
                  <card.icon className="h-4 w-4 text-slate-400" />
                  <p className="numeric-ltr mt-2 text-xl font-bold text-slate-900">{card.value}</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 overflow-hidden rounded-2xl bg-white shadow-sm">
              <h2 className="border-b border-slate-100 px-5 py-4 text-sm font-bold text-slate-900">
                {t('staffEarnings.byStaff', 'By staff member')}
              </h2>
              {data.staff.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-slate-500" data-testid="staff-earnings-empty">
                  {t('staffEarnings.empty', 'No bookings for this month yet')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-start">{t('walkIn.staff', 'Staff member')}</th>
                        <th className="px-4 py-2 text-end">{t('staffEarnings.onlineBookings', 'Online')}</th>
                        <th className="px-4 py-2 text-end">{t('staffEarnings.commissionShort', 'Commission')}</th>
                        <th className="px-4 py-2 text-end">{t('staffEarnings.walkInsShort', 'Walk-in')}</th>
                        <th className="px-4 py-2 text-end">{t('staffEarnings.total', 'Total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.staff.map((s) => (
                        <tr key={s.staff_id} className="border-t border-slate-100" data-testid={`staff-earnings-row-${s.staff_id}`}>
                          <td className="px-4 py-3">
                            <span className="font-bold text-slate-800">{s.staff_name}</span>
                            {s.role && <span className="block text-[11px] text-slate-400">{s.role}</span>}
                          </td>
                          <td className="numeric-ltr px-4 py-3 text-end text-slate-600">
                            {s.online_net} {t('common.currency', 'KWD')}
                            <span className="block text-[11px] text-slate-400">{s.online_bookings} {t('walkIn.bookingsLabel', 'bookings')}</span>
                          </td>
                          <td className="numeric-ltr px-4 py-3 text-end text-rose-600">-{s.commission} {t('common.currency', 'KWD')}</td>
                          <td className="numeric-ltr px-4 py-3 text-end text-amber-700">
                            {s.walk_in_earnings} {t('common.currency', 'KWD')}
                            <span className="block text-[11px] text-slate-400">{s.walk_ins} {t('walkIn.bookingsLabel', 'bookings')}</span>
                          </td>
                          <td className="numeric-ltr px-4 py-3 text-end font-bold text-slate-900">{s.total_net} {t('common.currency', 'KWD')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-400" data-testid="staff-earnings-note">
              {t('staffEarnings.note', 'Commission applies to online bookings only — walk-ins are commission free.')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default StaffEarnings;
